import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { siteConfig } from "@/data/siteConfig";
import { Reveal } from "./shared";
import { Mail, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Thank you for reaching out.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-charcoal py-24 lg:py-36 grain relative overflow-hidden">
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Contact</p>
              <h2 className="font-display text-4xl font-semibold tracking-tight text-cream sm:text-5xl">
                Partner, support, or inquire.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-cream/70">
                Families, schools, partners, and supporters are welcome. Reach out and a member of the Club will respond.
              </p>
              <div className="mt-10 space-y-6">
                <a href={`mailto:${siteConfig.contact.email}`} data-testid="contact-email-link" className="flex items-center gap-4 text-cream hover:text-gold transition-colors">
                  <Mail size={20} className="text-gold" />
                  <span>{siteConfig.contact.email}</span>
                </a>
                <div className="flex items-center gap-4 text-cream">
                  <MapPin size={20} className="text-gold" />
                  <span>{siteConfig.contact.address}</span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <input data-testid="contact-name" name="name" value={form.name} onChange={onChange} placeholder="Name" className="w-full border border-white/20 bg-transparent px-4 py-3 text-cream outline-none transition-colors focus:border-gold placeholder:text-cream/40" />
                  <input data-testid="contact-email" name="email" type="email" value={form.email} onChange={onChange} placeholder="Email" className="w-full border border-white/20 bg-transparent px-4 py-3 text-cream outline-none transition-colors focus:border-gold placeholder:text-cream/40" />
                </div>
                <input data-testid="contact-subject" name="subject" value={form.subject} onChange={onChange} placeholder="Subject" className="w-full border border-white/20 bg-transparent px-4 py-3 text-cream outline-none transition-colors focus:border-gold placeholder:text-cream/40" />
                <textarea data-testid="contact-message" name="message" value={form.message} onChange={onChange} rows={5} placeholder="Message" className="w-full resize-none border border-white/20 bg-transparent px-4 py-3 text-cream outline-none transition-colors focus:border-gold placeholder:text-cream/40" />
                <button data-testid="contact-submit-btn" type="submit" disabled={sending} className="bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-navy transition-colors duration-300 hover:bg-cream disabled:opacity-60">
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
